import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff } from "lucide-react";
import PageSeo from "../components/PageSeo";
import TurnstileField from "../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../turnstileConfig";
import { isApiConfigured, postResetPassword } from "../apiClient";
import Nd1AuthShell from "../nd1/components/Nd1AuthShell";
import Nd1ResetPasswordMarketing from "../nd1/components/Nd1ResetPasswordMarketing";
import "../nd1/nd1.css";

function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => {
    const raw = searchParams.get("token");
    return raw ? String(raw).trim() : "";
  }, [searchParams]);

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    if (!tokenFromUrl) {
      setSubmitError("This page needs a valid link from your reset email.");
      return;
    }
    if (password.length < 8) {
      setSubmitError("Password must be at least 8 characters.");
      return;
    }
    if (password !== password2) {
      setSubmitError("Passwords do not match.");
      return;
    }

    if (!isApiConfigured()) {
      setSubmitError("The app is not connected to the API. Password reset is unavailable in this mode.");
      return;
    }

    setSubmitting(true);
    try {
      if (isTurnstileEnabled() && !turnstileToken) {
        throw new Error(TURNSTILE_REQUIRED_MESSAGE);
      }
      const turnstilePayload =
        isTurnstileEnabled() && turnstileToken ? String(turnstileToken) : undefined;
      await postResetPassword({
        token: tokenFromUrl,
        password,
        turnstileToken: turnstilePayload,
      });
      setDone(true);
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } catch (err) {
      let raw = err instanceof Error ? err.message : "Something went wrong";
      if (!raw || raw === "Something went wrong") {
        raw = "Something went wrong. Please try again.";
      }
      if (raw === "Failed to fetch" || /networkerror|load failed|fetch/i.test(raw)) {
        raw =
          "Could not reach the server. Check your connection and try again, or request a new reset link.";
      }
      setSubmitError(formatTurnstileErrorForUser(raw));
      setTurnstileKey((k) => k + 1);
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const showTurnstile = isTurnstileEnabled() && isApiConfigured();

  const formCard = (
    <section className="nd1-auth-card nd1-auth-card--signin">
      <Link className="nd1-auth-back" to="/signin">
        <ArrowLeft className="nd1-icon" aria-hidden="true" />
        Back to sign in
      </Link>
      <p className="nd1-auth-card__eyebrow">Reset password</p>
      <h2>{done ? "Password updated!" : "Choose a new password"}</h2>

      {!tokenFromUrl && !done ? (
        <div role="alert">
          <p className="nd1-auth-card__lede">
            This link is missing a reset token. Open the link from your email, or request a new reset
            from sign in.
          </p>
          <p className="nd1-auth-verify-help">
            <Link className="nd1-auth-link-inline" to="/forgot-password">
              Request a new link
            </Link>
          </p>
        </div>
      ) : null}

      {done ? (
        <>
          <p className="nd1-auth-card__lede">
            Your password was updated. You can sign in with your new password.
          </p>
          <p className="nd1-auth-verify-help">
            <Link className="nd1-auth-link-inline" to="/signin">
              Go to sign in
            </Link>
          </p>
        </>
      ) : null}

      {tokenFromUrl && !done ? (
        <>
          <p className="nd1-auth-card__lede">
            Enter a new password for your account (at least 8 characters).
          </p>
          <form
            id="jobuwin-reset-password-form"
            className="nd1-auth-form"
            onSubmit={handleSubmit}
          >
            <label htmlFor="resetPassword">New password</label>
            <div className="nd1-auth-field nd1-auth-field--password">
              <input
                id="resetPassword"
                name="resetPassword"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
              />
              <button
                type="button"
                className="nd1-auth-field__toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="nd1-icon" aria-hidden="true" />
                ) : (
                  <Eye className="nd1-icon" aria-hidden="true" />
                )}
              </button>
            </div>
            <label htmlFor="resetPassword2">Confirm new password</label>
            <div className="nd1-auth-field nd1-auth-field--password">
              <input
                id="resetPassword2"
                name="resetPassword2"
                type={showPassword2 ? "text" : "password"}
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                minLength={8}
                required
              />
              <button
                type="button"
                className="nd1-auth-field__toggle"
                onClick={() => setShowPassword2((prev) => !prev)}
                aria-label={showPassword2 ? "Hide password" : "Show password"}
              >
                {showPassword2 ? (
                  <EyeOff className="nd1-icon" aria-hidden="true" />
                ) : (
                  <Eye className="nd1-icon" aria-hidden="true" />
                )}
              </button>
            </div>

            {!showTurnstile && submitError ? (
              <p className="nd1-auth-alert" role="alert">
                {submitError}
              </p>
            ) : null}

            {!showTurnstile ? (
              <button
                type="submit"
                className="nd1-btn nd1-btn--primary nd1-auth-submit"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Update password"}
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </button>
            ) : null}
          </form>

          {showTurnstile ? (
            <div className="nd1-signup-captcha">
              {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="nd1-auth-alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <TurnstileField
                tokenSessionId={0}
                onTokenChange={(token) => {
                  setTurnstileToken(token);
                  if (token) {
                    setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                  }
                }}
                widgetKey={`reset-password-${turnstileKey}`}
                options={{ action: "reset-password" }}
              />
              <p className="nd1-turnstile-note">
                Security check: making sure you are a genuine human :-)
              </p>
              {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
                <p className="nd1-auth-alert" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button
                type="submit"
                form="jobuwin-reset-password-form"
                className="nd1-btn nd1-btn--primary nd1-auth-submit"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Update password"}
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );

  return (
    <Nd1AuthShell hideFooter>
      <PageSeo
        title="Reset password"
        description="Choose a new JOBUWIN password and sign back in to your account."
        path="/reset-password"
        canonicalPath="/reset-password"
        noindex
      />
      <main id="nd1-main" className="nd1-shell nd1-auth-shell nd1-auth-shell--signin">
        <div className="nd1-auth-panel">
          <Nd1ResetPasswordMarketing />
          {formCard}
        </div>
      </main>
    </Nd1AuthShell>
  );
}

export default ResetPasswordPage;
